// The map, as a map (docs/plans/2026-10-17-map-view.md): the streets drawn flat and named, for the minimap in the HUD,
// the map window (M) and the title screen. It is drawn as the operation map on a dispatcher's desk
// (docs/plans/2026-10-18-dharna-144.md): asphalt ink on newsprint, restricted compounds hatched, key buildings solid,
// the crowd in metro yellow, the police in barricade blue, the square in riso red. The static part is painted once
// per scale from the map's vectors (roads, footprints, the zone) and cached; what moves (people, gates, the stage, your
// pin) goes on top every frame, in screen px.
import type { GameMap, Vec } from '../shared/map';
import { ChaloGeo, ChaloSnap, CODE_GATE } from '../shared/chalo';
import { enclosed } from './render';
import { MARKERS, drawBadge, markerFor, metroShut } from './hud';
import { FONT, INK, MAP, box, hatch } from './ui';

/** world px -> screen px: sx = ox + x * s, sy = oy + y * s */
export interface Xf { ox: number; oy: number; s: number }

/** What moves, handed over by main.ts each frame. */
export interface MapLive {
  me: Vec & { face: number };
  team: number;
  /** other players, tolis and walkers: type as in the snapshot (0 protester, 1 police, 2 walker, 3 toli); crowd: a walker who is the stage's kafila */
  others: (Vec & { type: number; name?: string; crowd?: boolean })[];
  /** the world rectangle on screen */
  cam: { x0: number; y0: number; x1: number; y1: number };
  /** the stage's dim layer (one px per tile) and where the zone is, while a stage is on */
  dim: HTMLCanvasElement | null;
  zone: Vec | null;
  /** Chalo: the rings, the snapshot, and the blackout's seconds left (0 or absent: none; it hides every marker) */
  chalo: { geo: ChaloGeo; ch: ChaloSnap | null; dark?: number } | null;
  pin: Vec | null;
}

// the operation map's inks (src/client/ui.ts INK): newsprint ground, asphalt roads, blocks a shade under the paper
const { newsprint: PAPER, newsprint2: PAPER_2, asphalt: ASPHALT, inkDim: INK_DIM, metro: METRO, barricade: BLUE, riso: RISO } = INK;
const GROUND: Record<string, string> = { '.': MAP.paper, p: MAP.park, W: MAP.paper, G: ASPHALT, M: ASPHALT, B: MAP.block, '=': ASPHALT, P: RISO };
const ROAD_MIN_PX: Record<string, number> = { trunk: 4, primary: 4, secondary: 3.2, tertiary: 2.4, pedestrian: 1.2 };
const MAJOR = new Set(['trunk', 'primary', 'secondary', 'tertiary']);
/** ordinary footprints are dark ink-grey blocks, as on the reference sheet; heritage and the yantras are the map's key locations, solid ink */
const BLOCK_FILL: Record<string, string> = { shop: MAP.blockAlt, plain: MAP.block, concrete: MAP.block, plaster: MAP.blockAlt, heritage: ASPHALT, yantra: ASPHALT };
const KEY = new Set(['heritage', 'yantra']);
const ART_MAX = 4096; // largest side of a cached painting, in px

/** The restricted-area hatch as a repeating pattern, in painting px. */
function hatchPattern(g: CanvasRenderingContext2D, colour: string, gap: number, lw: number): CanvasPattern {
  const c = document.createElement('canvas'); c.width = c.height = gap;
  const h = c.getContext('2d')!;
  h.strokeStyle = colour; h.lineWidth = lw; h.lineCap = 'square'; h.beginPath();
  for (const o of [-gap, 0, gap]) { h.moveTo(o, gap); h.lineTo(o + gap, 0); }
  h.stroke();
  return g.createPattern(c, 'repeat')!;
}

const metreOf = (m: GameMap) => m.tile / (m.mpt || 1);

// ------------------------------------------------------------------ the painting

/** The static map painted at a few scales (px per tile), each kept once made. */
export class MapArt {
  private cache = new Map<number, HTMLCanvasElement>();
  /** roadScale: the roads' share of their real width; the title screen draws them as thinner ink lines */
  constructor(readonly m: GameMap, readonly o: { roadScale?: number } = {}) {}

  /** The painting closest above `ppt` px per tile, in steps of two, no wider than ART_MAX. */
  at(ppt: number): HTMLCanvasElement {
    const top = ART_MAX / Math.max(this.m.w, this.m.h);
    let level = 1;
    while (level < ppt && level * 2 <= top) level *= 2;
    if (level < ppt) level = Math.min(top, ppt);
    let c = this.cache.get(level);
    if (!c) { c = this.paint(level); this.cache.set(level, c); }
    return c;
  }

  private paint(ppt: number): HTMLCanvasElement {
    const m = this.m, vec = !!m.roadLines?.length, blocks = !!m.buildings?.length;
    // tiles first, one px each, smoothed up: roads, the zone and buildings start as ground when their vectors follow.
    // The walled compounds (docs/plans/2026-10-18-no-go.md) and wall tiles go on a second layer, hatched below.
    const src = document.createElement('canvas'); src.width = m.w; src.height = m.h;
    const mask = document.createElement('canvas'); mask.width = m.w; mask.height = m.h;
    const sg = src.getContext('2d')!, img = sg.createImageData(m.w, m.h);
    const mg = mask.getContext('2d')!, mim = mg.createImageData(m.w, m.h);
    const rgb: Record<string, number[]> = {};
    for (const [k, v] of Object.entries(GROUND)) rgb[k] = [1, 3, 5].map((i) => parseInt(v.slice(i, i + 2), 16));
    let restricted = 0;
    for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) {
      let ch = m.rows[y][x];
      if ((vec && (ch === '=' || ch === 'P')) || (blocks && (ch === 'B' || ch === 'M'))) ch = '.';
      const c = rgb[ch] || rgb['.'], i = (y * m.w + x) * 4;
      img.data[i] = c[0]; img.data[i + 1] = c[1]; img.data[i + 2] = c[2]; img.data[i + 3] = 255;
      if (ch === 'W' || (ch !== 'p' && enclosed(m, x, y))) { mim.data[i + 3] = 255; restricted++; }
    }
    sg.putImageData(img, 0, 0); mg.putImageData(mim, 0, 0);
    const c = document.createElement('canvas');
    c.width = Math.round(m.w * ppt); c.height = Math.round(m.h * ppt);
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
    g.drawImage(src, 0, 0, c.width, c.height);
    if (restricted) { // restricted ground: ink hatching, cut to the compounds' tiles
      const hc = document.createElement('canvas'); hc.width = c.width; hc.height = c.height;
      const hg = hc.getContext('2d')!, gap = Math.max(5, Math.min(9, ppt * 0.9));
      hg.fillStyle = hatchPattern(hg, 'rgba(14,12,9,.42)', gap, Math.max(1, gap / 6)); hg.fillRect(0, 0, hc.width, hc.height);
      hg.globalCompositeOperation = 'destination-in'; hg.imageSmoothingEnabled = false; hg.drawImage(mask, 0, 0, hc.width, hc.height);
      g.drawImage(hc, 0, 0);
    }
    // the parks: a light halftone screen, open ground that is not street
    const parks: [number, number][] = [];
    for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) if (m.rows[y][x] === 'p') parks.push([x, y]);
    if (parks.length) {
      const dot = document.createElement('canvas'), ds = Math.max(4, Math.round(Math.min(8, ppt * 0.6)));
      dot.width = dot.height = ds;
      const dg = dot.getContext('2d')!; dg.fillStyle = 'rgba(14,12,9,.28)'; dg.beginPath(); dg.arc(ds / 2, ds / 2, Math.max(0.7, ds / 7), 0, Math.PI * 2); dg.fill();
      g.save(); g.beginPath(); for (const [x, y] of parks) g.rect(x * ppt, y * ppt, ppt + 0.5, ppt + 0.5); g.clip();
      g.fillStyle = g.createPattern(dot, 'repeat')!; g.fillRect(0, 0, c.width, c.height); g.restore();
    }
    g.scale(ppt, ppt); // from here on, tile units
    const ppm = ppt / (m.mpt || 1); // px per metre in this painting
    const line = (pts: number[]) => { g.beginPath(); g.moveTo(pts[0], pts[1]); for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]); };
    const poly = (pts: number[] | [number, number][]) => {
      const f = (pts as unknown[]).flat() as number[];
      g.moveTo(f[0], f[1]); for (let i = 2; i < f.length; i += 2) g.lineTo(f[i], f[i + 1]); g.closePath();
    };
    g.lineCap = 'round'; g.lineJoin = 'round';

    if (blocks) { // footprints under the roads, so a road's ink is never broken by a block that overlaps it
      g.lineWidth = Math.min(0.1, 1 / ppt); g.strokeStyle = 'rgba(14,12,9,.8)';
      for (const b of m.buildings!) {
        const style = m.buildingStyles?.[b.s] || 'plain';
        g.beginPath(); poly(b.o); for (const h of b.i || []) poly(h);
        g.fillStyle = BLOCK_FILL[style] || BLOCK_FILL.plain;
        g.fill('evenodd'); if (!KEY.has(style)) g.stroke();
      }
    }
    // the compound walls (vector, on the right-of-way edge): a fine ink line, so the hatch has an edge
    if (m.walls?.length) {
      g.strokeStyle = ASPHALT; g.lineWidth = Math.max(0.06, 1.1 / ppt); g.lineCap = 'butt';
      for (const w of m.walls) { g.beginPath(); g.moveTo(w.p[0] / m.tile, w.p[1] / m.tile); for (let i = 2; i < w.p.length; i += 2) g.lineTo(w.p[i] / m.tile, w.p[i + 1] / m.tile); g.stroke(); }
      g.lineCap = 'round';
    }
    if (vec) { // every road in asphalt ink: majors thick, lanes thin, footpaths dashed
      const k = this.o.roadScale ?? 1, width = (r: NonNullable<GameMap['roadLines']>[0]) => Math.max(r.w * ppm * k, (ROAD_MIN_PX[r.c] ?? 1.6) * Math.min(1, k * 1.6)) / ppt;
      g.strokeStyle = ASPHALT;
      for (const r of m.roadLines!) {
        if (r.c !== 'pedestrian') continue;
        g.lineWidth = width(r); g.setLineDash([3 / ppt + width(r), 2.5 / ppt + width(r)]); line(r.l); g.stroke();
      }
      g.setLineDash([]);
      for (const r of m.roadLines!) { if (r.c === 'pedestrian') continue; g.lineWidth = width(r); line(r.l); g.stroke(); }
    }
    // the square: riso red over its road, hatched, with a hard edge. It is what the game is about
    if (m.zone?.length) {
      g.beginPath(); for (const z of m.zone) poly(z);
      g.fillStyle = 'rgba(227,52,47,.82)'; g.fill();
      g.save(); g.clip();
      g.strokeStyle = 'rgba(14,12,9,.45)'; g.lineWidth = 1 / ppt; g.beginPath();
      for (let d = -m.h; d < m.w; d += 6 / ppt) { g.moveTo(d, m.h); g.lineTo(d + m.h, 0); }
      g.stroke(); g.restore();
      g.beginPath(); for (const z of m.zone) poly(z); g.strokeStyle = ASPHALT; g.lineWidth = 2 / ppt; g.stroke();
    }
    return c;
  }
}

// ------------------------------------------------------------------ names: where each label goes, worked out once

export interface RoadLabel { name: string; x: number; y: number; ang: number; len: number; major: boolean }
interface PlaceLabel { name: string; x: number; y: number; kind: 'landmark' | 'yantra' }

/** For each named road, anchors along its longest straight runs, at least 220 m apart. World px. */
export function roadLabels(m: GameMap): RoadLabel[] {
  if (!m.roadLines) return [];
  const t = m.tile, metre = metreOf(m), all: RoadLabel[] = [];
  for (const r of m.roadLines) {
    if (!r.n) continue;
    for (let i = 2; i < r.l.length; i += 2) {
      const ax = r.l[i - 2] * t, ay = r.l[i - 1] * t, bx = r.l[i] * t, by = r.l[i + 1] * t, len = Math.hypot(bx - ax, by - ay);
      if (len < 25 * metre) continue;
      let ang = Math.atan2(by - ay, bx - ax);
      if (ang > Math.PI / 2) ang -= Math.PI; else if (ang < -Math.PI / 2) ang += Math.PI; // never upside down
      all.push({ name: r.n, x: (ax + bx) / 2, y: (ay + by) / 2, ang, len, major: MAJOR.has(r.c) && r.c !== 'tertiary' });
    }
  }
  all.sort((a, b) => b.len - a.len);
  const out: RoadLabel[] = [];
  for (const a of all) if (!out.some((o) => o.name === a.name && Math.hypot(o.x - a.x, o.y - a.y) < 220 * metre)) out.push(a);
  return out;
}

/** Named buildings that aren't shops, once per name, at the centre of the largest footprint with it. World px. */
function placeLabels(m: GameMap): PlaceLabel[] {
  const best = new Map<string, { area: number; x: number; y: number; kind: PlaceLabel['kind'] }>();
  for (const b of m.buildings || []) {
    const style = m.buildingStyles?.[b.s];
    if (!b.n || style === 'shop') continue;
    let a = 0, cx = 0, cy = 0;
    const o = b.o, n = o.length / 2;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n, cr = o[i * 2] * o[j * 2 + 1] - o[j * 2] * o[i * 2 + 1];
      a += cr; cx += (o[i * 2] + o[j * 2]) * cr; cy += (o[i * 2 + 1] + o[j * 2 + 1]) * cr;
    }
    if (Math.abs(a) < 1e-9) continue;
    const area = Math.abs(a / 2), x = (cx / (3 * a)) * m.tile, y = (cy / (3 * a)) * m.tile;
    const prev = best.get(b.n);
    if (!prev || area > prev.area) best.set(b.n, { area, x, y, kind: style === 'yantra' ? 'yantra' : 'landmark' });
  }
  return [...best.entries()].sort((p, q) => q[1].area - p[1].area).map(([name, v]) => ({ name, x: v.x, y: v.y, kind: v.kind }));
}

// ------------------------------------------------------------------ drawing helpers (screen px)

const LIGHT = new Set<string>([PAPER, PAPER_2, METRO]);
/** A label with a halo of the opposite ink: asphalt type on a newsprint halo, or newsprint type on an asphalt one. */
function text(g: CanvasRenderingContext2D, s: string, x: number, y: number, fill: string, font: string, halo = 3) {
  g.font = font; g.lineWidth = halo; g.strokeStyle = LIGHT.has(fill) ? ASPHALT : PAPER; g.fillStyle = fill; g.lineJoin = 'round';
  g.strokeText(s, x, y); g.fillText(s, x, y);
}

/** The crowd's glyph, as on the operation map: a metro-yellow disc ringed in ink with an ink dot at its heart. */
export function crowdDot(g: CanvasRenderingContext2D, x: number, y: number, r: number, alpha = 1) {
  g.globalAlpha = alpha;
  g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fillStyle = METRO; g.fill();
  g.lineWidth = Math.max(1, r * 0.32); g.strokeStyle = ASPHALT; g.stroke();
  g.beginPath(); g.arc(x, y, Math.max(0.8, r * 0.34), 0, Math.PI * 2); g.fillStyle = ASPHALT; g.fill();
  g.globalAlpha = 1;
}

/** Police: a barricade-blue block with an ink edge. */
function policeDot(g: CanvasRenderingContext2D, x: number, y: number, r: number) {
  g.fillStyle = BLUE; g.fillRect(x - r, y - r, r * 2, r * 2);
  g.lineWidth = 1; g.strokeStyle = ASPHALT; g.strokeRect(x - r, y - r, r * 2, r * 2);
}

/** A metro station: the map's ring, newsprint inside an ink ring with an ink dot. */
export function metroRing(g: CanvasRenderingContext2D, x: number, y: number, r: number) {
  g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fillStyle = PAPER; g.fill();
  g.lineWidth = Math.max(1.5, r * 0.36); g.strokeStyle = ASPHALT; g.stroke();
  g.beginPath(); g.arc(x, y, r * 0.38, 0, Math.PI * 2); g.fillStyle = ASPHALT; g.fill();
}

/** You: an asphalt arrowhead filled metro yellow, pointing where you face, on a hard newsprint disc. */
function drawMe(g: CanvasRenderingContext2D, x: number, y: number, face: number, r: number) {
  g.save(); g.translate(x, y);
  g.beginPath(); g.arc(0, 0, r * 1.75, 0, Math.PI * 2); g.fillStyle = PAPER; g.fill(); g.lineWidth = 2; g.strokeStyle = ASPHALT; g.stroke();
  g.rotate(face);
  g.beginPath(); g.moveTo(r * 1.35, 0); g.lineTo(-r * 0.9, r * 0.95); g.lineTo(-r * 0.4, 0); g.lineTo(-r * 0.9, -r * 0.95); g.closePath();
  g.fillStyle = METRO; g.fill(); g.lineWidth = 1.5; g.strokeStyle = ASPHALT; g.stroke();
  g.restore();
}

/** The pin: a flag on a pole, its foot at (x, y). The same shape as the cursor. */
export function drawPinMark(g: CanvasRenderingContext2D, x: number, y: number, h: number) {
  const k = h / 22;
  g.save(); g.translate(x, y); g.lineCap = 'butt'; g.lineJoin = 'miter';
  g.fillStyle = ASPHALT; g.fillRect(-4 * k, -1 * k, 8 * k, 2 * k);
  g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -22 * k); g.strokeStyle = ASPHALT; g.lineWidth = 2.6 * k; g.stroke();
  g.beginPath(); g.moveTo(1 * k, -22 * k); g.lineTo(13 * k, -22 * k); g.lineTo(9.5 * k, -18 * k); g.lineTo(13 * k, -14 * k); g.lineTo(1 * k, -14 * k); g.closePath();
  g.fillStyle = METRO; g.fill(); g.strokeStyle = ASPHALT; g.lineWidth = 1.4 * k; g.stroke();
  g.restore();
}

/** A cordon layer for the map: one px per tile, barricade blue where any ring's tape runs. Made once per geo. */
const cordonArt = new WeakMap<ChaloGeo, HTMLCanvasElement>();
function cordonOf(m: GameMap, G: ChaloGeo): HTMLCanvasElement {
  let c = cordonArt.get(G);
  if (!c) {
    c = document.createElement('canvas'); c.width = m.w; c.height = m.h;
    const g = c.getContext('2d')!, img = g.createImageData(m.w, m.h);
    for (let i = 0; i < G.cordon.length; i++) if (G.cordon[i]) { img.data[i * 4] = 0x0A; img.data[i * 4 + 1] = 0x24; img.data[i * 4 + 2] = 0xB0; img.data[i * 4 + 3] = 235; }
    g.putImageData(img, 0, 0);
    cordonArt.set(G, c);
  }
  return c;
}

/**
 * What moves, over the painting, through the transform: the stage's dim, the rings and their gates, people, you, the
 * pin and the part of the street on screen. `big` is the map window, which also names things.
 */
function drawLive(g: CanvasRenderingContext2D, m: GameMap, xf: Xf, L: MapLive, big: boolean) {
  const X = (x: number) => xf.ox + x * xf.s, Y = (y: number) => xf.oy + y * xf.s, ts = m.tile * xf.s;
  g.imageSmoothingEnabled = false;
  if (L.dim) { g.globalAlpha = 0.42; g.drawImage(L.dim, X(0), Y(0), m.width * xf.s, m.height * xf.s); g.globalAlpha = 1; } // a grey screen on paper, not night
  const dark = !!L.chalo?.dark;
  if (L.chalo) {
    const G = L.chalo.geo, ch = L.chalo.ch;
    g.drawImage(cordonOf(m, G), X(0), Y(0), m.width * xf.s, m.height * xf.s);
    G.cps.forEach((cp, k) => { // in the blackout every gap looks shut
      const st = ch && !dark ? CODE_GATE[ch.cs[k]?.[0] ?? 0] : 'closed';
      g.fillStyle = st === 'open' ? PAPER : st === 'back' ? BLUE : st === 'game' ? METRO : RISO;
      for (const i of cp.gaps) g.fillRect(X((i % m.w) * m.tile), Y(Math.floor(i / m.w) * m.tile), Math.ceil(ts), Math.ceil(ts));
    });
  }
  g.imageSmoothingEnabled = true;
  if (L.zone) { // the stage's goal: a square riso target that breathes
    const r = (big ? 9 : 5) + (big ? 3 : 2) * Math.sin(performance.now() / 300);
    g.strokeStyle = RISO; g.lineWidth = big ? 3 : 2; g.strokeRect(X(L.zone.x) - r, Y(L.zone.y) - r, r * 2, r * 2);
  }
  // people: walkers faint and only in the window, tolis as knots of the crowd, players by side
  const dot = big ? 3 : 2;
  for (const o of dark ? [] : L.others) {
    if (o.type === 2 && o.crowd) { crowdDot(g, X(o.x), Y(o.y), dot * 0.75); continue; } // the kafila: the crowd's glyph, smaller
    if (o.type === 2) { if (!big) continue; g.fillStyle = 'rgba(92,88,80,.55)'; g.beginPath(); g.arc(X(o.x), Y(o.y), dot * 0.6, 0, Math.PI * 2); g.fill(); continue; }
    if (o.type === 1) policeDot(g, X(o.x), Y(o.y), dot * 0.95);
    else crowdDot(g, X(o.x), Y(o.y), o.type === 3 ? dot * 1.35 : dot);
  }
  if (big) {
    g.textAlign = 'left'; g.textBaseline = 'middle';
    for (const o of dark ? [] : L.others) if (o.name) text(g, o.name, X(o.x) + 7, Y(o.y), o.type === 1 ? BLUE : ASPHALT, FONT.body(11, 600));
  }
  if (L.chalo && !dark) drawChaloMarkers(g, xf, L.chalo.geo, L.chalo.ch, big); // over the people: the states must read
  // the street on screen, so a pan or zoom shows where you are looking
  const c = L.cam, rx = X(Math.max(0, c.x0)), ry = Y(Math.max(0, c.y0)), rw = X(Math.min(m.width, c.x1)) - rx, rh = Y(Math.min(m.height, c.y1)) - ry;
  if (rw > 0 && rh > 0) { g.strokeStyle = ASPHALT; g.lineWidth = 2; g.setLineDash(big ? [6, 4] : [4, 3]); g.strokeRect(rx, ry, rw, rh); g.setLineDash([]); }
  if (L.pin) drawPinMark(g, X(L.pin.x), Y(L.pin.y), big ? 24 : 14);
  drawMe(g, X(L.me.x), Y(L.me.y), L.me.face, big ? 6 : 4);
}

/**
 * Chalo's markers (asset plan section 6): each checkpoint by its state (kenney-minimap-pack glyphs: an X shut, a dot
 * while a game runs, an open doorway, a doorway closing), the stage, the van, the bus, and the metro gates (struck
 * through once shut). Screen px, through the transform.
 */
function drawChaloMarkers(g: CanvasRenderingContext2D, xf: Xf, G: ChaloGeo, ch: ChaloSnap | null, big: boolean) {
  const X = (x: number) => xf.ox + x * xf.s, Y = (y: number) => xf.oy + y * xf.s, b = big ? 20 : 12, t = ch?.t ?? 0;
  for (const e of G.entries) {
    if (!e.name || !/gate/i.test(e.name)) continue; // the metro gates (the street entries need no mark)
    const shut = metroShut(e, t), x = X(e.x), y = Y(e.y);
    metroRing(g, x, y, b * 0.42);
    if (shut) { g.strokeStyle = RISO; g.lineWidth = big ? 3 : 2; g.beginPath(); g.moveTo(x - b / 2, y + b / 2); g.lineTo(x + b / 2, y - b / 2); g.stroke(); }
  }
  drawBadge(g, 'bus', BLUE, X(G.bus.x), Y(G.bus.y), b);
  // the van parks beside the stage: its badge sits a little off so both read
  drawBadge(g, 'mm-stage', METRO, X(G.centre.x) - b * 0.55, Y(G.centre.y), b);
  drawBadge(g, 'mm-van', BLUE, X(G.van.x) + b * 0.55, Y(G.van.y), b);
  G.cps.forEach((cp, k) => {
    const mk = markerFor(ch?.cs[k]?.[0] ?? 0);
    drawBadge(g, mk.tile, mk.colour, X(cp.at.x), Y(cp.at.y), b);
  });
}

/** The marker legend rows for the map window and the minimap's key. */
const MARKER_ROWS = Object.values(MARKERS);

// ------------------------------------------------------------------ the minimap

/** How much of the map the minimap shows: all of it when it is small, else this many metres around you. */
const MINI_SPAN_M = 640;

/** The minimap in the HUD. Shows the whole map when it is small, else a window around you; click it for the map. */
export class Minimap {
  /** where it was last drawn, in screen px, so a click on it can open the map */
  rect = { x: 0, y: 0, w: 0, h: 0 };
  constructor(readonly m: GameMap, readonly art: MapArt) {}

  draw(g: CanvasRenderingContext2D, x: number, yBottom: number, size: number, dpr: number, L: MapLive) {
    const m = this.m, metre = metreOf(m), spanPx = MINI_SPAN_M * metre;
    const whole = !m.mpt || Math.max(m.width, m.height) <= spanPx * 1.15;
    let w: number, h: number, wx0: number, wy0: number, s: number;
    if (whole) {
      s = size / Math.max(m.width, m.height); w = Math.round(m.width * s); h = Math.round(m.height * s); wx0 = 0; wy0 = 0;
    } else {
      s = size / spanPx; w = h = size;
      wx0 = Math.max(0, Math.min(m.width - spanPx, L.me.x - spanPx / 2)); wy0 = Math.max(0, Math.min(m.height - spanPx, L.me.y - spanPx / 2));
    }
    const y = yBottom - h;
    this.rect = { x, y, w, h };
    const xf: Xf = { ox: x - wx0 * s, oy: y - wy0 * s, s };
    g.save();
    // panel: the map as a printed sheet: ink border, hard shadow
    box(g, x - 3, y - 3, w + 6, h + 6, PAPER, 3, 5);
    g.beginPath(); g.rect(x, y, w, h); g.clip();
    const art = this.art.at(s * m.tile * dpr), k = art.width / m.width; // painting px per world px
    g.drawImage(art, wx0 * k, wy0 * k, (w / s) * k, (h / s) * k, x, y, w, h);
    drawLive(g, m, xf, L, false);
    if (L.chalo?.dark) { // the director's blackout: the sheet is inked over but for you and the cordon
      g.fillStyle = 'rgba(14,12,9,.78)'; g.fillRect(x, y, w, h);
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.font = FONT.display(22); g.fillStyle = METRO; g.fillText('BLACKOUT', x + w / 2, y + h / 2 - 8);
      g.font = FONT.mono(12); g.fillStyle = PAPER; g.fillText(`${Math.ceil(L.chalo.dark)} s`, x + w / 2, y + h / 2 + 12);
    }
    g.restore();
    // Chalo: a key for the checkpoint markers beside the minimap, so a state can be named from the minimap alone
    if (L.chalo && !L.chalo.dark) {
      const rh = 18, kx = x + w + 12, ky = yBottom - MARKER_ROWS.length * rh - 4;
      box(g, kx - 5, ky - 5, 96, MARKER_ROWS.length * rh + 10, PAPER, 2, 4);
      g.textAlign = 'left'; g.textBaseline = 'middle'; g.font = FONT.mono(11); g.fillStyle = ASPHALT;
      MARKER_ROWS.forEach((mk, i) => {
        const cy = ky + i * rh + rh / 2;
        drawBadge(g, mk.tile, mk.colour, kx + 7, cy, 14);
        g.fillText(mk.label.toUpperCase(), kx + 20, cy);
      });
    }
    // north, and how to open the map: a tab on the sheet's top edge
    g.textBaseline = 'middle'; g.textAlign = 'center';
    g.fillStyle = ASPHALT; g.fillRect(x + w - 18, y, 18, 18);
    g.font = FONT.display(15); g.fillStyle = PAPER; g.fillText('N', x + w - 9, y + 10);
    g.textAlign = 'left';
    const t = 'M · MAP'; g.font = FONT.mono(10, 600);
    const tw = g.measureText(t).width;
    box(g, x - 3, y - 21, tw + 12, 18, ASPHALT, 0, 0);
    g.fillStyle = METRO; g.fillText(t, x + 3, y - 12);
    g.textBaseline = 'alphabetic';
  }

  hit(px: number, py: number): boolean {
    const r = this.rect;
    return px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h;
  }
}

// ------------------------------------------------------------------ the map window

const LAYERS = ['names', 'places', 'people'] as const;
type Layer = (typeof LAYERS)[number];

/**
 * The map window (M): the whole map, pannable and zoomable, with street and landmark names, the places from OSM, the
 * people, and a pin you drop with a click. The game keeps running behind it; your keys still walk you.
 */
export class MapWindow {
  open = false;
  /** world px at the centre of the canvas, and screen px per world px */
  private v = { x: 0, y: 0, s: 1 };
  private fit = 1;
  /** opened before: later opens keep where you left the map */
  private seen = false;
  private W = 0; private H = 0; private dpr = 1;
  private g: CanvasRenderingContext2D;
  private roads: RoadLabel[]; private places: PlaceLabel[];
  private ptr = new Map<number, { x: number; y: number }>();
  private drag: { x: number; y: number; vx: number; vy: number; moved: number } | null = null;
  private pinch: { d: number; s: number } | null = null;
  private hover: { x: number; y: number } | null = null;
  readonly show: Record<Layer, boolean> = { names: true, places: true, people: true };
  /** a click on the map: drop the pin there, or lift it (world px, null to clear) */
  onPin: (p: Vec | null) => void = () => {};
  /** where you are, for the centre button and for opening on you */
  me: () => Vec = () => ({ x: 0, y: 0 });
  pin: Vec | null = null;

  constructor(readonly m: GameMap, readonly art: MapArt, private root: HTMLElement, private canvas: HTMLCanvasElement, title: string) {
    this.g = canvas.getContext('2d')!;
    this.roads = roadLabels(m); this.places = placeLabels(m);
    root.querySelector<HTMLElement>('[data-title]')!.textContent = title;
    root.querySelector<HTMLElement>('[data-close]')!.onclick = () => this.close();
    root.querySelector<HTMLElement>('[data-me]')!.onclick = () => { const p = this.me(); this.v.x = p.x; this.v.y = p.y; this.v.s = Math.max(this.v.s, this.fit * 2.5); };
    root.querySelector<HTMLElement>('[data-fit]')!.onclick = () => this.fitAll();
    root.querySelector<HTMLElement>('[data-zin]')!.onclick = () => this.zoomAt(this.W / 2, this.H / 2, 1.4);
    root.querySelector<HTMLElement>('[data-zout]')!.onclick = () => this.zoomAt(this.W / 2, this.H / 2, 1 / 1.4);
    root.querySelectorAll<HTMLButtonElement>('[data-layer]').forEach((b) => {
      const k = b.dataset.layer as Layer;
      b.setAttribute('aria-pressed', String(this.show[k]));
      b.onclick = () => { this.show[k] = !this.show[k]; b.setAttribute('aria-pressed', String(this.show[k])); };
    });
    root.addEventListener('click', (e) => { if (e.target === root) this.close(); }); // the backdrop
    canvas.addEventListener('pointerdown', (e) => this.down(e));
    canvas.addEventListener('pointermove', (e) => this.move(e));
    canvas.addEventListener('pointerup', (e) => this.up(e));
    canvas.addEventListener('pointercancel', (e) => { this.ptr.delete(e.pointerId); this.drag = null; this.pinch = null; });
    canvas.addEventListener('pointerleave', () => { this.hover = null; });
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect(), dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      this.zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.002)));
    }, { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  toggle() { if (this.open) this.close(); else this.show_(); }
  show_() {
    this.open = true; this.root.hidden = false;
    this.resize();
    // open on you, a little zoomed in on a big map, all of it on a small one
    const p = this.me();
    if (!this.seen) {
      this.seen = true; this.fitAll();
      if (Math.max(this.m.width, this.m.height) > 1500 * metreOf(this.m)) { this.v.s = this.fit * 2.5; this.v.x = p.x; this.v.y = p.y; this.clamp(); }
    }
    this.root.querySelector<HTMLElement>('[data-close]')!.focus();
  }
  close() { this.open = false; this.root.hidden = true; this.ptr.clear(); this.drag = null; this.pinch = null; }

  /** + / - / 0 / C while the window is open. */
  key(code: string, key: string): boolean {
    if (key === '+' || key === '=' || code === 'NumpadAdd') { this.zoomAt(this.W / 2, this.H / 2, 1.4); return true; }
    if (key === '-' || key === '_' || code === 'NumpadSubtract') { this.zoomAt(this.W / 2, this.H / 2, 1 / 1.4); return true; }
    if (key === '0' || code === 'Numpad0') { this.fitAll(); return true; }
    if (code === 'KeyC') { (this.root.querySelector('[data-me]') as HTMLElement).click(); return true; }
    if (code === 'KeyP') { this.onPin(null); return true; }
    return false;
  }

  resize() {
    const r = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(2, devicePixelRatio || 1); this.W = r.width; this.H = r.height;
    this.canvas.width = Math.round(this.W * this.dpr); this.canvas.height = Math.round(this.H * this.dpr);
    this.fit = Math.min(this.W / this.m.width, this.H / this.m.height) * 0.94;
  }
  fitAll() { this.v.s = this.fit; this.v.x = this.m.width / 2; this.v.y = this.m.height / 2; }

  private xf(): Xf { return { ox: this.W / 2 - this.v.x * this.v.s, oy: this.H / 2 - this.v.y * this.v.s, s: this.v.s }; }
  private world(sx: number, sy: number): Vec { return { x: this.v.x + (sx - this.W / 2) / this.v.s, y: this.v.y + (sy - this.H / 2) / this.v.s }; }
  private zoomAt(sx: number, sy: number, f: number) {
    const before = this.world(sx, sy), max = 8 / metreOf(this.m); // at most 8 screen px per metre
    this.v.s = Math.max(this.fit * 0.8, Math.min(max, this.v.s * f));
    const after = this.world(sx, sy);
    this.v.x += before.x - after.x; this.v.y += before.y - after.y;
    this.clamp();
  }
  private clamp() {
    this.v.x = Math.max(0, Math.min(this.m.width, this.v.x)); this.v.y = Math.max(0, Math.min(this.m.height, this.v.y));
  }

  private local(e: PointerEvent) { const r = this.canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }
  private down(e: PointerEvent) {
    this.canvas.setPointerCapture(e.pointerId);
    const p = this.local(e); this.ptr.set(e.pointerId, p);
    if (this.ptr.size === 1) this.drag = { x: p.x, y: p.y, vx: this.v.x, vy: this.v.y, moved: 0 };
    if (this.ptr.size === 2) { const [a, b] = [...this.ptr.values()]; this.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: this.v.s }; this.drag = null; }
  }
  private move(e: PointerEvent) {
    const p = this.local(e); this.hover = p;
    if (!this.ptr.has(e.pointerId)) return;
    this.ptr.set(e.pointerId, p);
    if (this.pinch && this.ptr.size === 2) {
      const [a, b] = [...this.ptr.values()], f = (this.pinch.s * Math.hypot(a.x - b.x, a.y - b.y) / this.pinch.d) / this.v.s;
      this.zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, f);
    } else if (this.drag) {
      this.drag.moved = Math.max(this.drag.moved, Math.hypot(p.x - this.drag.x, p.y - this.drag.y));
      this.v.x = this.drag.vx - (p.x - this.drag.x) / this.v.s; this.v.y = this.drag.vy - (p.y - this.drag.y) / this.v.s;
      this.clamp();
    }
  }
  private up(e: PointerEvent) {
    const p = this.local(e), d = this.drag;
    this.ptr.delete(e.pointerId);
    if (this.ptr.size < 2) this.pinch = null;
    if (!this.ptr.size) this.drag = null;
    if (!d || d.moved > 6 || e.button !== 0) return;
    // a tap: lift the pin when it is tapped, else drop it here (on the map only)
    const w = this.world(p.x, p.y);
    if (this.pin && Math.hypot((this.pin.x - w.x) * this.v.s, (this.pin.y - 12 / this.v.s - w.y) * this.v.s) < 16) this.onPin(null);
    else if (w.x >= 0 && w.y >= 0 && w.x <= this.m.width && w.y <= this.m.height) this.onPin(w);
  }

  draw(L: MapLive) {
    if (!this.open) return;
    const r = this.canvas.getBoundingClientRect();
    if (Math.abs(r.width - this.W) > 0.5 || Math.abs(r.height - this.H) > 0.5) { const old = this.fit; this.resize(); if (this.v.s <= old * 1.01) this.fitAll(); }
    const g = this.g, m = this.m, xf = this.xf(), metre = metreOf(m), ppm = xf.s * metre;
    g.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    // the desk under the sheet: newsprint a shade down, halftone, so the map's own edge reads
    g.fillStyle = PAPER_2; g.fillRect(0, 0, this.W, this.H);
    const art = this.art.at(xf.s * m.tile * this.dpr);
    box(g, xf.ox - 3, xf.oy - 3, m.width * xf.s + 6, m.height * xf.s + 6, PAPER, 0, 8);
    g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
    g.drawImage(art, xf.ox, xf.oy, m.width * xf.s, m.height * xf.s);
    g.strokeStyle = ASPHALT; g.lineWidth = 3; g.strokeRect(xf.ox - 1.5, xf.oy - 1.5, m.width * xf.s + 3, m.height * xf.s + 3);
    const X = (x: number) => xf.ox + x * xf.s, Y = (y: number) => xf.oy + y * xf.s;
    const mx0 = X(0), my0 = Y(0), mx1 = X(m.width), my1 = Y(m.height);
    // the legend, scale, north arrow and stamp's corners are taken before any name
    const taken: number[][] = [[0, this.H - 16 - 17 * 18 - 40, 215, this.H], [this.W - 230, this.H - 70, this.W, this.H], [this.W - 64, 0, this.W, 70]]; // label boxes on screen, so names never sit on each other
    const free = (x0: number, y0: number, x1: number, y1: number) => {
      if (x0 < Math.max(0, mx0) || y0 < Math.max(0, my0) || x1 > Math.min(this.W, mx1) || y1 > Math.min(this.H, my1)) return false; // on the map and the screen
      if (taken.some((b) => x0 < b[2] && x1 > b[0] && y0 < b[3] && y1 > b[1])) return false;
      taken.push([x0, y0, x1, y1]); return true;
    };

    // the zone's name, big, first: it is what the game is about. A tag pasted over the square
    if (m.zone?.length) {
      let sx = 0, sy = 0, n = 0;
      for (const z of m.zone) for (const [x, y] of z) { sx += x; sy += y; n++; }
      const zx = X((sx / n) * m.tile), zy = Y((sy / n) * m.tile), label = m.name.toUpperCase();
      g.font = FONT.display(22);
      const tw = g.measureText(label).width + 16;
      if (free(zx - tw / 2, zy - 40, zx + tw / 2, zy - 10)) {
        box(g, zx - tw / 2, zy - 40, tw, 28, ASPHALT, 0, 4);
        g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = PAPER; g.fillText(label, zx, zy - 24);
      }
    }
    if (this.show.names) {
      g.textAlign = 'center'; g.textBaseline = 'middle';
      for (const rl of this.roads) {
        if (!rl.major && ppm < 0.7) continue; // lanes once there is room for them
        const font = rl.major ? FONT.display(15) : FONT.body(10, 600), label = rl.major ? rl.name.toUpperCase() : rl.name;
        g.font = font;
        const tw = g.measureText(label).width, sl = rl.len * xf.s;
        if (tw > sl * 1.15) continue; // only where the road is long enough on screen to carry it
        const sx = X(rl.x), sy = Y(rl.y), hw = (Math.abs(Math.cos(rl.ang)) * tw + Math.abs(Math.sin(rl.ang)) * 12) / 2, hh = (Math.abs(Math.sin(rl.ang)) * tw + Math.abs(Math.cos(rl.ang)) * 12) / 2;
        if (!free(sx - hw, sy - hh, sx + hw, sy + hh)) continue;
        g.save(); g.translate(sx, sy); g.rotate(rl.ang);
        text(g, label, 0, rl.major ? 1 : 0, rl.major ? PAPER : ASPHALT, font, rl.major ? 3.5 : 3); // newsprint letters on the road's own ink
        g.restore();
      }
      for (const p of this.places) {
        if (p.kind === 'landmark' && ppm < 0.9) continue;
        const font = p.kind === 'yantra' ? FONT.display(13) : FONT.body(10, 600), label = p.kind === 'yantra' ? p.name.toUpperCase() : p.name;
        g.font = font; const tw = g.measureText(label).width, sx = X(p.x), sy = Y(p.y);
        if (free(sx - tw / 2, sy - 7, sx + tw / 2, sy + 7)) text(g, label, sx, sy, p.kind === 'yantra' ? PAPER : ASPHALT, font);
      }
    }
    if (this.show.places && m.pois.length) {
      const r = ppm > 1.5 ? 4.5 : 3.5;
      for (const p of m.pois) {
        const sx = X(p.x), sy = Y(p.y);
        if (sx < -10 || sy < -10 || sx > this.W + 10 || sy > this.H + 10) continue;
        poiMark(g, p.g, sx, sy, r);
      }
      if (ppm > 1.6) {
        g.textAlign = 'left'; g.textBaseline = 'middle';
        for (const p of m.pois) {
          if (!p.name) continue;
          const sx = X(p.x) + r + 4, sy = Y(p.y); g.font = FONT.body(10, 600);
          const tw = g.measureText(p.name).width;
          if (free(sx, sy - 7, sx + tw, sy + 7)) text(g, p.name, sx, sy, p.g === 'police' ? BLUE : ASPHALT, FONT.body(10, 600));
        }
      }
    }
    if (L.chalo) { // checkpoint names by their gaps
      g.textAlign = 'center'; g.textBaseline = 'middle';
      L.chalo.geo.cps.forEach((cp, k) => {
        const st = L.chalo!.ch && !L.chalo!.dark ? CODE_GATE[L.chalo!.ch.cs[k]?.[0] ?? 0] : 'closed', sx = X(cp.at.x), sy = Y(cp.at.y) - 18;
        g.font = FONT.mono(11, 600);
        const tw = g.measureText(cp.name).width;
        if (free(sx - tw / 2 - 4, sy - 8, sx + tw / 2 + 4, sy + 8)) {
          box(g, sx - tw / 2 - 4, sy - 8, tw + 8, 16, st === 'open' ? PAPER : st === 'back' ? BLUE : st === 'game' ? METRO : ASPHALT, 1, 2);
          g.fillStyle = st === 'open' || st === 'game' ? ASPHALT : PAPER; g.fillText(cp.name, sx, sy + 1);
        }
      });
    }
    drawLive(g, m, xf, this.show.people ? L : { ...L, others: [] }, true);
    if (L.pin) { // how far, under the pin
      const d = Math.hypot(L.pin.x - L.me.x, L.pin.y - L.me.y) / metre;
      g.textAlign = 'center'; g.textBaseline = 'top';
      const t = `${Math.round(d)} M`; g.font = FONT.mono(12, 600);
      const tw = g.measureText(t).width;
      box(g, X(L.pin.x) - tw / 2 - 5, Y(L.pin.y) + 5, tw + 10, 18, METRO, 2, 2);
      g.fillStyle = ASPHALT; g.textBaseline = 'middle'; g.fillText(t, X(L.pin.x), Y(L.pin.y) + 14.5);
    }
    this.chrome(g, ppm, metre, L);
    g.textBaseline = 'alphabetic';
  }

  /** North, the scale bar, the legend, the stamp and the credit: the parts of a map that aren't the map. */
  private chrome(g: CanvasRenderingContext2D, ppm: number, metre: number, L: MapLive) {
    const W = this.W, H = this.H;
    // north: an ink block with the arrow cut out of it
    box(g, W - 52, 12, 40, 50, ASPHALT, 0, 4);
    g.beginPath(); g.moveTo(W - 32, 18); g.lineTo(W - 22, 42); g.lineTo(W - 32, 36); g.lineTo(W - 42, 42); g.closePath(); g.fillStyle = PAPER; g.fill();
    g.textAlign = 'center'; g.textBaseline = 'middle'; g.font = FONT.display(15); g.fillStyle = METRO; g.fillText('N', W - 32, 53);
    // scale: a round number of metres about 120 px long, in alternating ink and paper blocks as a surveyor draws it
    const want = 120 / ppm, steps = [10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000];
    const mtr = steps.find((v) => v >= want * 0.7) ?? 2000, bw = mtr * ppm, sx = W - 24 - bw, sy = H - 34;
    box(g, sx - 12, sy - 26, bw + 24, 46, PAPER, 2, 4);
    g.textAlign = 'left'; g.textBaseline = 'alphabetic'; g.font = FONT.mono(10, 600); g.fillStyle = ASPHALT;
    g.fillText('SCALE', sx, sy - 12);
    for (let i = 0; i < 4; i++) { g.fillStyle = i % 2 ? PAPER : ASPHALT; g.fillRect(sx + (bw / 4) * i, sy - 5, bw / 4, 6); }
    g.strokeStyle = ASPHALT; g.lineWidth = 1.5; g.strokeRect(sx, sy - 5, bw, 6);
    g.textAlign = 'center'; g.fillStyle = ASPHALT; g.font = FONT.mono(10, 500);
    g.fillText('0', sx, sy + 13); g.fillText(mtr >= 1000 ? `${mtr / 1000} KM` : `${mtr} M`, sx + bw, sy + 13);
    // legend: only what this map and mode show
    const rows: [string, (x: number, y: number) => void][] = [
      ['YOU', (x, y) => drawMe(g, x, y, -Math.PI / 2, 4.2)],
      ['CROWD / PROTESTERS', (x, y) => crowdDot(g, x, y, 4.5)],
      ['POLICE', (x, y) => policeDot(g, x, y, 4)],
    ];
    if (this.m.roadLines?.length) {
      rows.push(['MAJOR ROAD', (x, y) => { g.fillStyle = ASPHALT; g.fillRect(x - 8, y - 2.5, 16, 5); }]);
      rows.push(['LANE / FOOTPATH', (x, y) => { g.fillStyle = ASPHALT; g.fillRect(x - 8, y - 1, 7, 2); g.fillRect(x + 1, y - 1, 7, 2); }]);
    }
    if (this.m.pois.some((p) => p.g === 'metro')) rows.push(['METRO STATION', (x, y) => metroRing(g, x, y, 5)]);
    rows.push(['KEY LOCATION', (x, y) => { g.fillStyle = ASPHALT; g.fillRect(x - 6, y - 5, 12, 10); }]);
    if (this.m.walls?.length || this.m.nogo) rows.push(['RESTRICTED AREA', (x, y) => { g.fillStyle = PAPER; g.fillRect(x - 7, y - 5, 14, 10); hatch(g, x - 7, y - 5, 14, 10, ASPHALT, 4, 1); g.strokeStyle = ASPHALT; g.lineWidth = 1; g.strokeRect(x - 7, y - 5, 14, 10); }]);
    if (L.chalo) {
      rows.push(['TOLIS', (x, y) => crowdDot(g, x, y, 5.5)]);
      rows.push(['POLICE BARRICADE', (x, y) => { g.fillStyle = BLUE; g.fillRect(x - 8, y - 3, 16, 6); }]);
      for (const mk of MARKER_ROWS) rows.push([`CHECKPOINT: ${mk.label.toUpperCase()}`, (x, y) => drawBadge(g, mk.tile, mk.colour, x, y, 15)]);
      rows.push(['STAGE', (x, y) => drawBadge(g, 'mm-stage', METRO, x, y, 15)]);
      rows.push(['POLICE VAN', (x, y) => drawBadge(g, 'mm-van', BLUE, x, y, 15)]);
      rows.push(['DETENTION BUS', (x, y) => drawBadge(g, 'bus', BLUE, x, y, 15)]);
    }
    if (this.m.zone?.length) rows.push(['THE SQUARE', (x, y) => { g.fillStyle = RISO; g.fillRect(x - 7, y - 5, 14, 10); g.strokeStyle = ASPHALT; g.lineWidth = 1.5; g.strokeRect(x - 7, y - 5, 14, 10); }]);
    if (L.dim) rows.push(['OUTSIDE THE STAGE', (x, y) => { g.fillStyle = 'rgba(14,12,9,.36)'; g.fillRect(x - 7, y - 5, 14, 10); }]);
    rows.push(['YOUR PIN', (x, y) => drawPinMark(g, x - 2, y + 7, 14)]);
    const lh = 19, lw = 196, lx = 14, head = 26, ly = H - 16 - rows.length * lh - head - 8;
    box(g, lx, ly, lw, rows.length * lh + head + 8, PAPER, 3, 5);
    g.fillStyle = ASPHALT; g.fillRect(lx, ly, lw, head);
    g.textAlign = 'left'; g.textBaseline = 'middle'; g.font = FONT.display(18); g.fillStyle = PAPER; g.fillText('LEGEND', lx + 10, ly + head / 2 + 1);
    g.font = FONT.mono(10, 600); g.fillStyle = ASPHALT;
    rows.forEach(([label, icon], i) => {
      const y = ly + head + 4 + lh * i + lh / 2;
      icon(lx + 18, y); g.font = FONT.mono(10, 600); g.fillStyle = ASPHALT; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(label, lx + 34, y);
    });
    // the credit, wherever the map is shown (ODbL)
    if (this.m.attribution) { g.textAlign = 'right'; g.textBaseline = 'alphabetic'; g.font = FONT.mono(10); g.fillStyle = INK_DIM; g.fillText(this.m.attribution, W - 14, H - 5); }
    // what's under the pointer: the nearest named road, on a yellow tag
    if (this.hover && this.show.names) {
      const w = this.world(this.hover.x, this.hover.y), n = nearestRoad(this.m, w, 18 * metre);
      if (n) {
        g.font = FONT.display(16); const tw = g.measureText(n.toUpperCase()).width;
        box(g, this.hover.x + 14, this.hover.y + 6, tw + 14, 22, METRO, 2, 3);
        g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = ASPHALT; g.fillText(n.toUpperCase(), this.hover.x + 21, this.hover.y + 18);
      }
    }
  }
}

/** An OSM point marker by its group, in the operation map's symbols. Screen px. */
function poiMark(g: CanvasRenderingContext2D, group: string, x: number, y: number, r: number) {
  if (group === 'metro') { metroRing(g, x, y, r * 1.25); return; }
  if (group === 'police') { policeDot(g, x, y, r * 0.9); return; }
  if (group === 'gate' || group === 'barrier') { g.beginPath(); g.moveTo(x, y - r); g.lineTo(x + r, y); g.lineTo(x, y + r); g.lineTo(x - r, y); g.closePath(); g.fillStyle = group === 'gate' ? METRO : PAPER; g.fill(); g.lineWidth = 1.2; g.strokeStyle = ASPHALT; g.stroke(); return; }
  if (group === 'government' || group === 'heritage') { g.fillStyle = ASPHALT; g.fillRect(x - r * 0.8, y - r * 0.8, r * 1.6, r * 1.6); return; }
  g.beginPath(); g.arc(x, y, r * 0.75, 0, Math.PI * 2); g.fillStyle = PAPER; g.fill(); g.lineWidth = 1.5; g.strokeStyle = ASPHALT; g.stroke();
}

/** The name of the named road nearest to p within `within` world px, if any. */
export function nearestRoad(m: GameMap, p: Vec, within: number): string | null {
  let best: string | null = null, bd = within;
  const t = m.tile;
  for (const r of m.roadLines || []) {
    if (!r.n) continue;
    for (let i = 2; i < r.l.length; i += 2) {
      const ax = r.l[i - 2] * t, ay = r.l[i - 1] * t, bx = r.l[i] * t, by = r.l[i + 1] * t;
      const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy || 1, k = Math.max(0, Math.min(1, ((p.x - ax) * dx + (p.y - ay) * dy) / L2));
      const d = Math.hypot(p.x - ax - dx * k, p.y - ay - dy * k);
      if (d < bd) { bd = d; best = r.n; }
    }
  }
  return best;
}
